import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1786026632864-6dc820662aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxsb2dpc3RpY3MlMjBkZWxpdmVyeSUyMHJpZGVyJTIwd29ya2VyJTIwd2FyZWhvdXNlJTIwcGFja2FnaW5nfGVufDB8fHx8MTc5MTA2MjM2NXww&ixlib=rb-4.1.0&q=85";

const checks = [
  { title: "Aadhar Card Verified", sub: "Government identity confirmed for every associate" },
  { title: "PAN Card Verified", sub: "Tax identity documented and on file" },
  { title: "Active Bank Account", sub: "Salary-ready, financially documented staff" },
  { title: "Local Route Expertise", sub: "Vetted for area and navigation knowledge" },
  { title: "Smartphone App Handling", sub: "Trained on delivery applications and DRS flow" },
  { title: "Sorting & Packing Skills", sub: "Hub-ready from day one" },
];

export const WhyUs = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section id="why-us" ref={ref} className="relative bg-[#0a0f1d] py-20 sm:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#d4af37] mb-4">Verification & Compliance</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase text-slate-100 leading-[0.95] mb-8">
              Stamped. Verified. <span className="text-stroke-gold">Deployment-Ready.</span>
            </h2>
          </motion.div>

          <div className="space-y-1">
            {checks.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="flex items-start gap-4 py-4 border-b border-white/8 group"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-slate-100 uppercase tracking-wide">{c.title}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{c.sub}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <span data-testid="why-us-verification-badge" className="text-[10px] font-bold tracking-[0.2em] uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-3 py-2 rounded-sm">
              100% Document Verified
            </span>
            <span data-testid="why-us-scaling-badge" className="text-[10px] font-bold tracking-[0.2em] uppercase bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/30 px-3 py-2 rounded-sm">
              30–40% Surge in 48 Hours
            </span>
          </div>
        </div>

        <motion.div style={{ y: imgY }} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-[#3b82f6]/25 rounded-sm" />
            <div className="relative overflow-hidden rounded-sm">
              <img src={IMG} alt="Warehouse packing operations" className="w-full h-[26rem] sm:h-[32rem] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1d]/80 via-transparent to-transparent" />
              <div className="absolute top-5 left-5 bg-[#0a0f1d]/90 backdrop-blur border border-[#d4af37]/40 px-4 py-3 rounded-sm rotate-[-3deg]">
                <p className="font-display text-lg font-extrabold uppercase text-[#d4af37] leading-none tracking-wide">Verified</p>
                <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">Majisa Quality Stamp</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
